import { useCallback, useEffect, useState } from 'react';
import { AgenciesService } from '@services/requests/housien-services/AgenciesService';
import type { InvoiceItem } from '@services/requests/housien-services/AgenciesService.subscription';

export interface UseSubscriptionInvoicesResult {
  invoices: InvoiceItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  isLoading: boolean;
  isError: boolean;
}

const DEFAULT_PAGE_SIZE = 5;

/**
 * Fetches paginated invoices for an agency subscription.
 *
 * Wraps GET /agencies/{agencyId}/subscription/invoices and handles client-side
 * pagination state. Re-fetches automatically when page changes.
 */
export function useSubscriptionInvoices(
  agencyId: string,
  pageSize = DEFAULT_PAGE_SIZE,
): UseSubscriptionInvoicesResult {
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const handleSetPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    AgenciesService.getSubscriptionInvoices(agencyId, page, pageSize)
      .then((result) => {
        if (!cancelled) {
          setInvoices(result.invoices);
          setTotal(result.total);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsError(true);
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [agencyId, page, pageSize]);

  return { invoices, total, page, pageSize, setPage: handleSetPage, isLoading, isError };
}
